/**
 * useHelpDesk Composable
 *
 * Manages the open/close state of the HelpDeskDialog and tracks the
 * context (userName, userEmail, currentPath, isLoggedIn) captured at the moment the
 * dialog is opened.
 *
 * Usage
 * ─────
 * ```ts
 * // In a layout component, composable, or store (singleton pattern)
 * const helpDesk = useHelpDesk(async (payload) => {
 *   await api.post('/helpdesk/tickets', payload);
 * });
 *
 * // Open for a logged-in user
 * helpDesk.open('Dr. Sarah Mueller', 'sarah@example.com', '/dashboard/orders/create');
 *
 * // Open for a non-logged-in user (no name/path required)
 * helpDesk.openAnonymous();
 *
 * // In the template
 * <HelpDeskDialog
 *   v-model:visible="helpDesk.visible.value"
 *   :is-logged-in="helpDesk.isLoggedIn.value"
 *   :user-name="helpDesk.userName.value"
 *   :user-email="helpDesk.userEmail.value"
 *   :current-path="helpDesk.currentPath.value"
 *   :loading="helpDesk.loading.value"
 *   :auto-close-on-submit="false"
 *   :show-success-toast="false"
 *   @submit="helpDesk.handleSubmit"
 * />
 * ```
 *
 * Because Vue's `ref`s are returned, this composable is safe to use as
 * either a local instance or a shared module-level singleton.
 */
import { ref } from 'vue';
import type { HelpDeskTicketPayload } from '../components/ui-library/HelpDeskDialog.vue';

export type HelpDeskSubmitHandler = (payload: HelpDeskTicketPayload) => Promise<void>;

/**
 * Factory that creates an isolated help-desk state.
 * Call once at the layout/app level and pass the returned API down via
 * provide/inject or props.
 *
 * @param onSubmit - Required async handler called with the ticket payload.
 *   Must return a Promise — the composable awaits it to control loading state
 *   and dialog close timing. Throw (or reject) to keep the dialog open on error.
 */
export function useHelpDesk(onSubmit: HelpDeskSubmitHandler) {
  const visible = ref(false);
  const isLoggedIn = ref(true);
  const userName = ref('');
  const userEmail = ref('');
  const currentPath = ref('');
  const loading = ref(false);

  /**
   * Open the help-desk dialog for a **logged-in** user.
   *
   * @param name  – currently logged-in user's display name
   * @param email – logged-in user's email address
   * @param path  – current route path (e.g. `route.fullPath`)
   */
  function open(name: string, email: string, path: string) {
    isLoggedIn.value = true;
    userName.value = name;
    userEmail.value = email;
    currentPath.value = path;
    visible.value = true;
  }

  /**
   * Open the help-desk dialog for a **non-logged-in** (anonymous) user.
   * The dialog will show editable Name and required Email fields instead
   * of the read-only Name + Current Path.
   */
  function openAnonymous() {
    isLoggedIn.value = false;
    userName.value = '';
    userEmail.value = '';
    currentPath.value = '';
    visible.value = true;
  }

  /** Close the dialog without submitting */
  function close() {
    visible.value = false;
  }

  /**
   * Called when the form emits a `submit` event.
   * Delegates to the `onSubmit` callback provided to the composable.
   */
  async function handleSubmit(payload: HelpDeskTicketPayload) {
    // Defensive guard — unreachable when used from TypeScript but protects
    // plain-JS callers from a silent no-op masquerading as success.
    if (!onSubmit) {
      throw new Error(
        '[useHelpDesk] No onSubmit handler was provided. ' +
        'Pass an async handler to useHelpDesk(onSubmit) so tickets are actually submitted.',
      );
    }

    loading.value = true;
    try {
      await onSubmit(payload);
      visible.value = false;
    } finally {
      loading.value = false;
    }
  }

  return {
    visible,
    isLoggedIn,
    userName,
    userEmail,
    currentPath,
    loading,
    open,
    openAnonymous,
    close,
    handleSubmit,
  };
}
